import {
  FeatureFlagEnvironment,
  FeatureFlagProvider,
} from '@zip/feature-flag-react';
import { useAnalytics, useMerchantData } from 'contexts';
import { Environments } from 'enums';
import React, { createContext, useContext, useState } from 'react';

export type FeatureFlagsProviderProps = {
  children: React.ReactNode;
};

export type FeatureFlagsContextType = {
  merchantId: number | null;
  updateMerchantId: (id: number) => void;
};

// Context to track when feature flags are ready
const FeatureFlagsContext = createContext<FeatureFlagsContextType>(undefined);

export const useFeatureFlagsReady = (): FeatureFlagsContextType => {
  return useContext(FeatureFlagsContext);
};

function getEnvironment(): FeatureFlagEnvironment {
  const env = process.env.REACT_APP_ENVIRONMENT?.toLowerCase();

  switch (env) {
    case Environments.Sandbox:
      return FeatureFlagEnvironment.Sand;
    case Environments.Production:
      return FeatureFlagEnvironment.Prod;
    default:
      return FeatureFlagEnvironment.Dev;
  }
}

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({
  children,
}) => {
  const [merchantId, setMerchantId] = useState<number | null>(null);

  const { merchantIdentity, profileData } = useMerchantData();
  const environment = getEnvironment();
  const analytics = useAnalytics();

  const deploymentKey =
    process.env.REACT_APP_AMPLITUDE_FEATURE_FLAG_DEPLOYMENT_KEY ?? '';
  const companyId =
    merchantIdentity?.companyId ?? profileData?.merchantData?.companyId;

  const updateMerchantId = (id: number): void => {
    if (merchantId != id) {
      setMerchantId(id);
    }
  };

  return (
    <FeatureFlagsContext.Provider value={{ merchantId, updateMerchantId }}>
      <FeatureFlagProvider
        analytics={analytics.state}
        config={{
          deploymentKey,
          environment,
          disableExposureEvent: true,
        }}
        user={{
          userId: profileData?.userProfile?.userProfileId,
          properties: {
            merchantId: merchantId,
            companyId: companyId,
          },
        }}
      >
        {children}
      </FeatureFlagProvider>
    </FeatureFlagsContext.Provider>
  );
};
